import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/utils";
import {
  ReservedMessageIds,
  FixedMessageIds,
  NO_ANSWER_MSG_PREFIX,
  ChatMessage,
  isPushAction,
  isMessageAction,
  BRAIN_ACTIONS
} from "@/entities/messaging/types";
import { motion } from "framer-motion";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { simpleTime } from "@/utils/datetime";
import {
  getMeetingPrepGreetingDate,
  getMeetingPrepGreetingKey
} from "./meetingPrepGreeting";
import { hasRenderableDraft } from "@/entities/dreamerEmail/types";
import {
  Star,
  Copy,
  ThumbsUp,
  ThumbsDown,
  StarFilled,
  ThumbsUpFilled,
  ThumbsDownFilled,
  Check,
  Alert,
  Notification,
  InfoCircle,
  Refresh,
  DreamhubOutline
} from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import ChatSeparator from "./ChatSeparator";
import { useTranslation } from "react-i18next";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useAnalytics } from "@/services/analytics/hook";
import { useMessaging } from "@/entities/messaging/hooks";
import MarkdownRenderer from "@/components/ui/markdown-renderer";
import Text from "@/components/ui/text";
import {
  isDateSeparatorMessage,
  isThinkingMessage
} from "@/entities/messaging/helpers";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import TableMessage from "@/components/global/Chat/components/dreamerCharts/table/TableMessage";
import DreamerLeadActions from "@/components/global/Chat/components/chat/DreamerLeadActions";
import DreamerLeadConvertActions from "@/components/global/Chat/components/chat/DreamerLeadConvertActions";
import MeetingPrepCard from "@/components/global/Chat/components/meetingPrep/MeetingPrepCard";
import DraftEmailCard from "@/components/global/Chat/components/chat/DraftEmailCard";
import ChatErrorCard from "@/components/global/Chat/components/chat/ChatErrorCard";
import { isChatTurnMessage } from "@/components/global/Chat/components/chat/chatSlicer";
import ChartMessage from "@/components/global/Chat/components/dreamerCharts/hooks/components/ChartMessage";
import { isChartKind } from "@/components/global/Chat/chartKind";
import RecurringReportsModal from "@/components/global/Chat/components/dreamerReports/RecurringReportsModal";
import { ExpandedReportsModal } from "@/components/global/Chat/components/dreamerReports/ExpandedReportsModal";
import InlineScheduledReports from "@/components/global/Chat/components/dreamerReports/InlineScheduledReports";
import { Button } from "@/components/ui/button";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useDreamerCompleteTaskMutation, useDreamerReopenTaskMutation, useDreamerUndoTaskMutation, useRefreshFilteredTasks, useRemoveTaskFromLists } from "@/entities/tasks/hooks";
import { TaskData } from "@/entities/tasks/types";
import useBoundStore from "@/services/store/BoundStore";
import useAPI from "@/services/api/hook";
import toast from "react-hot-toast";

interface Props {
  message: ChatMessage;
  selectedUser: UserData;
  index: number;
  skipAnimation?: boolean;
  // Gate the enter animation on genuine newness, not mount — under virtualization an old message remounts on scroll-into-view.
  animateEntry?: boolean;
  // Legacy list only: framer `layout`/`exit` (they misfire under virtualization, where items mount/unmount on scroll).
  enableLayoutExit?: boolean;
  onRetry?: (originalMessage: string) => void;
  onRetryFailedAnswer?: (question: ChatMessage, errorMessageId: string) => void;
  onRegenerateDraft?: (messageId: string, guidance?: string) => void;
  onSelectDraftVersion?: (
    messageId: string,
    version: number,
    onFailure: () => void
  ) => void;
  isRegeneratingDraft?: boolean;
  // The in-app viewer's first name, for the meeting-prep greeting — passed down from a single
  // useAuth() call in the list rather than read here, so an auth-context change (token refresh,
  // multi-tab sync) doesn't re-render every mounted balloon and bypass this component's memo.
  recipientFirstName?: string;
  // Saves a /downloads/:id file linked in the message; created once in the list for the same reason.
  onDownloadAttachment?: (attachmentId: string) => Promise<void>;
}

function ChatBalloonComponent({
  message,
  selectedUser,
  index,
  skipAnimation = false,
  animateEntry = false,
  enableLayoutExit = false,
  onRetry,
  onRetryFailedAnswer,
  onRegenerateDraft,
  onSelectDraftVersion,
  isRegeneratingDraft,
  recipientFirstName,
  onDownloadAttachment
}: Props) {
  const { t } = useTranslation();
  const meetingPrepBrief = message.content?.meetingPrep;
  // Greeting sits above the card as a chat line; the date never carries a year (regardless
  // of which year the meeting falls in — the card title below carries the year instead).
  const meetingPrepGreetingDate = getMeetingPrepGreetingDate(
    meetingPrepBrief?.meetingDate
  );
  const meetingPrepGreeting = meetingPrepBrief
    ? t(
        getMeetingPrepGreetingKey(!!recipientFirstName, !!meetingPrepGreetingDate),
        {
          name: recipientFirstName,
          company: meetingPrepBrief.overview.companyName,
          date: meetingPrepGreetingDate
        }
      )
    : "";
  const emailDraft = message.emailDraft;
  const { updateMessageState, messages, waitingForMessage } = useChat();
  const analytics = useAnalytics();
  const { updateMessageStatus } = useMessaging();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isManageReportsModalOpen, setIsManageReportsModalOpen] =
    useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const dreamerUndo = useDreamerUndoTaskMutation();
  const dreamerComplete = useDreamerCompleteTaskMutation();
  const dreamerReopen = useDreamerReopenTaskMutation();
  const removeTask = useBoundStore((state) => state.tasks.removeEntity);
  const refreshTasks = useRefreshFilteredTasks();
  const removeTaskFromLists = useRemoveTaskFromLists();
  // content.action is a union (push affordance vs brain instruction); narrow once.
  const contentAction =
    message.content?.kind === "action" ? message.content.action : undefined;
  const pushAction = isPushAction(contentAction) ? contentAction : undefined;
  const messageAction = isMessageAction(contentAction) ? contentAction : undefined;
  const isDreamerUndoMessage =
    pushAction?.type === "dreamer_task_undo" && !!pushAction.taskId;
  const isDreamerReopenMessage =
    pushAction?.type === "dreamer_task_reopen" && !!pushAction.taskId;
  const isDreamerTaskActionMessage =
    isDreamerUndoMessage || isDreamerReopenMessage;
  const isDreamerLeadActionMessage =
    (pushAction?.type === "dreamer_lead_create" ||
      pushAction?.type === "dreamer_lead_create_manual") &&
    !!pushAction.candidateId;
  const isDreamerLeadConvertMessage =
    pushAction?.type === "dreamer_lead_convert" && !!pushAction.leadId;
  const { enabled: showMarkComplete } = useFeatureFlag(
    FeatureFlags.CRM_8136_MARK_COMPLETED_BUTTON
  );
  // Showing the card suppresses the markdown body, so an empty draft must not
  // qualify — that would leave the turn with nothing but the intro line.
  const renderableDraft = hasRenderableDraft(emailDraft) ? emailDraft : undefined;
  const isShowScheduledReports =
    messageAction?.action === BRAIN_ACTIONS.REPORTING_SCHEDULER_FILTER;
  const isManageScheduledReports =
    message.inlineAction === "manage-scheduled-reports";
  // Under My Reports the scheduled-reports card and the All set message's manage
  // button open the same table as the Reports tab's expand, on its scheduled filter.
  const { enabled: isMyReportsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  const [isExpandedReportsOpen, setIsExpandedReportsOpen] = useState(false);
  const opensReportsTable =
    (isShowScheduledReports || isManageScheduledReports) && isMyReportsEnabled;
  const { data: taskData, error: taskFetchError } = useAPI(
    "tasks/update",
    { method: "GET", pathParams: { taskId: pushAction?.taskId ?? "" } },
    { enabled: isDreamerTaskActionMessage, staleTime: Infinity }
  );
  const taskNotFound =
    (taskFetchError as { response?: { status?: number } } | null)?.response
      ?.status === 404;
  const taskCompleted = (taskData as TaskData | undefined)?.isCompleted === true;
  // Undo card: hide once the task is already deleted/completed (action no longer meaningful).
  // Reopen card: hide once the task has been reopened (isCompleted=false) or deleted.
  const hideUndoCard = isDreamerUndoMessage && (taskNotFound || taskCompleted);
  const hideReopenCard =
    isDreamerReopenMessage && (taskNotFound || (!!taskData && !taskCompleted));

  const handleNativeCopy = useCallback((event: React.ClipboardEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = event.currentTarget;

    const containerRange = document.createRange();
    containerRange.selectNodeContents(container);

    const clippedRange = document.createRange();

    if (
      range.compareBoundaryPoints(Range.START_TO_START, containerRange) >= 0
    ) {
      clippedRange.setStart(range.startContainer, range.startOffset);
    } else {
      clippedRange.setStart(
        containerRange.startContainer,
        containerRange.startOffset
      );
    }

    if (range.compareBoundaryPoints(Range.END_TO_END, containerRange) <= 0) {
      clippedRange.setEnd(range.endContainer, range.endOffset);
    } else {
      clippedRange.setEnd(
        containerRange.endContainer,
        containerRange.endOffset
      );
    }

    const text = clippedRange.toString().trimEnd();
    if (text) {
      const fragment = clippedRange.cloneContents();
      const wrapper = document.createElement("div");
      wrapper.appendChild(fragment);
      event.clipboardData.setData("text/html", wrapper.innerHTML);
      event.clipboardData.setData("text/plain", text);
      event.preventDefault();
    }
  }, []);

  const handleRetry = useCallback(() => {
    if (!onRetry) return;
    // Find the user message that preceded this stopped response
    const idx = messages.findIndex((m) => m.id === message.id);
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].user?.id === selectedUser.id) {
        onRetry(messages[i].message);
        return;
      }
    }
  }, [onRetry, messages, message.id, selectedUser.id]);

  // Where this card sits in the context's messages. A card shown for an
  // unanswered question is not in them, so it is placed right after the
  // question it is named after.
  const errorCardIndex = useMemo(() => {
    if (!message.isError) return -1;
    const cardIndex = messages.findIndex(
      (candidate) => candidate.id === message.id
    );
    if (cardIndex !== -1) return cardIndex;
    const questionIndex = messages.findIndex(
      (candidate) => `${NO_ANSWER_MSG_PREFIX}${candidate.id}` === message.id
    );
    return questionIndex === -1 ? -1 : questionIndex + 1;
  }, [message.isError, message.id, messages]);

  // Only the latest turn can be retried: once the conversation has moved on,
  // an answer to this question would land below the newer turns.
  const isLatestTurn = useMemo(
    () =>
      errorCardIndex !== -1 &&
      !messages
        .slice(errorCardIndex)
        .some(
          (laterMessage) =>
            laterMessage.id !== message.id && isChatTurnMessage(laterMessage)
        ),
    [errorCardIndex, message.id, messages]
  );

  // The question is the user's latest message before this card.
  const handleRetryFailedAnswer = useCallback(() => {
    if (!onRetryFailedAnswer) return;
    for (let i = errorCardIndex - 1; i >= 0; i--) {
      if (messages[i].user?.id === selectedUser.id) {
        onRetryFailedAnswer(messages[i], message.id);
        return;
      }
    }
  }, [
    onRetryFailedAnswer,
    errorCardIndex,
    messages,
    message.id,
    selectedUser.id
  ]);

  if (message.id === ReservedMessageIds.MSG_SEPARATOR) {
    let messageText: string = "";
    switch (message.message) {
      case FixedMessageIds.NEW_MESSAGE:
      case FixedMessageIds.NEW_MESSAGES:
        const messageCountType =
          message.message === FixedMessageIds.NEW_MESSAGE ? 1 : 10; // 10 is arbitrary
        messageText = t("chat.newMessages", { count: messageCountType });
    }

    if (!!messageText)
      return (
        <span className="flex flex-col items-stretch w-full py-1.5">
          <ChatSeparator text={messageText} textVariant="smBody" thick />
        </span>
      );
    return <></>;
  }

  if (isDateSeparatorMessage(message)) {
    return (
      <span className="flex flex-col items-stretch w-full py-1.5">
        <ChatSeparator text={message.message} />
      </span>
    );
  }

  if (!message.user) return;

  const isSelectedUser = message.user.id === selectedUser.id;
  const isDreamerUser =
    message.user.id === ReservedUserIds.DREAMER_CONVERSATIONAL_ID;

  // Shared so the draft-email opening renders exactly like any other message body.
  const messageMarkdownClassName = cn(
    isSelectedUser
      ? "bg-surface-elevation2 border border-border-border1 rounded-2xl p-[8px_12px_8px] px-4"
      : "bg-surface-elevation1 px-5",
    "select-text"
  );
  // Dreamer's own opening for a drafted email. Whitespace-only is as empty as missing —
  // either way the card gets the static intro. The `??` is not redundant with the `string`
  // type: this field crosses an unvalidated websocket boundary, same reason
  // isDateSeparatorMessage guards `message.id`.
  const draftAnswer = (message.message ?? "").trim() ? message.message : "";

  // Showing the card suppresses the full markdown body, so a drafted email is introduced by
  // Dreamer's own opening — `message` carries only that opening once the backend shrinks it.
  // An empty opening falls back to the static string so the card is never introduced by
  // blank space.
  const messageBody = renderableDraft ? (
    draftAnswer ? (
      <MarkdownRenderer
        content={draftAnswer}
        className={messageMarkdownClassName}
        data-test="draft-email-intro"
        onDownloadAttachment={onDownloadAttachment}
      />
    ) : (
      <Text
        as="p"
        variant="lgBody"
        className="px-5 whitespace-pre-line select-text"
        data-test="draft-email-intro"
      >
        {t("draftEmail.intro")}
      </Text>
    )
  ) : (
    <MarkdownRenderer
      content={message.message}
      className={messageMarkdownClassName}
      onDownloadAttachment={onDownloadAttachment}
    />
  );

  const handleCopy = async (messageText: string) => {
    const result = navigator.clipboard.writeText(messageText);
    result.then(() => {
      setIsCopied(true);
    });
  };

  function handleStar(message: ChatMessage): void {
    const toggledStar = !message.isStarred;
    updateMessageStatus(message.id, { isStarred: toggledStar });
    updateMessageState(message, toggledStar);
  }

  // Handler for both like and dislike buttons (see parameter)
  function handleLike(message: ChatMessage, likeButton: boolean): void {
    const currentLikeStatus = message.isLiked;
    let newStatus: boolean | null;
    if (likeButton) {
      newStatus = currentLikeStatus === true ? null : true;
    } else {
      newStatus = currentLikeStatus === false ? null : false;
    }
    analytics.track("dreamer_feedback", {
      like: newStatus,
      messageId: message.id
    });
    updateMessageStatus(message.id, { isLiked: newStatus });
    updateMessageState(message, undefined, newStatus);
  }

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
        setIsOpen(false);
      }, 2000);
    }
  }, [isCopied]);

  if (isHidden || hideUndoCard || hideReopenCard) return null;

  if (message.isError) {
    return (
      <div
        className="flex py-1.5 px-5 w-full"
        data-test={`chat-session-msg-index-${index}`}
      >
        <ChatErrorCard
          onRetry={handleRetryFailedAnswer}
          disabled={waitingForMessage || !isLatestTurn}
        />
      </div>
    );
  }

  // Enter animation plays once, only for genuinely-new Dreamer replies; `layout`/`exit` are legacy-only (they misfire under virtualization).
  const shouldAnimateEntry = animateEntry && !skipAnimation;
  let exitAnimation: Record<string, number> | undefined = undefined;
  if (enableLayoutExit) {
    exitAnimation = skipAnimation
      ? { opacity: 1 }
      : { opacity: 0, scale: 1, y: 1, x: 0 };
  }

  return (
    <motion.div
      key={message.id}
      layout={enableLayoutExit}
      initial={
        shouldAnimateEntry ? { opacity: 0, scale: 1, y: 50, x: 0 } : false
      }
      animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
      exit={exitAnimation}
      transition={
        enableLayoutExit
          ? {
              opacity: { duration: 0.3 },
              layout: { type: "spring", bounce: 0.3, duration: 0.3 }
            }
          : { opacity: { duration: 0.3 } }
      }
      style={{
        originX: 0.5,
        originY: 0.5
      }}
      className={cn(
        "flex flex-col gap-2 py-1.5 whitespace-pre-wrap",
        isSelectedUser ? "items-end" : "items-start"
      )}
      data-test={`chat-session-msg-index-${index}`}
    >
      <div
        className={cn(
          "flex gap-x-1 items-start w-full",
          isSelectedUser ? "flex-row-reverse" : "flex-row"
        )}
      >
        {isSelectedUser && (
          <UserAvatar
            size="L"
            className="mx-1 mt-1.5 mr-5"
            userData={message.user}
          />
        )}
        {isThinkingMessage(message) ? (
          <span className="flex flex-row align-start justify-start items-center gap-3 w-full px-5 py-2">
            <motion.span
              animate={{
                rotate: 360,
                scale: [1, 1.2, 1],
                filter: [
                  "drop-shadow(0 0 8px rgba(99, 102, 241, 0.5))",
                  "drop-shadow(0 0 20px rgba(99, 102, 241, 0.8))",
                  "drop-shadow(0 0 8px rgba(99, 102, 241, 0.5))"
                ]
              }}
              transition={{
                rotate: { duration: 4, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                filter: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
              className="flex items-center justify-center shrink-0 size-4"
            >
              <DreamhubOutline />
            </motion.span>
            <Text variant="bodySecondary" data-test="thought-message">
              {message.thoughtMessage || t("chat.thinking")}
            </Text>
          </span>
        ) : (
          <span
            className={cn(
              "flex flex-col justify-end items-start py-1.5 gap-1.5 grow min-w-0",
              "text-base text-normal leading-6"
            )}
          >
            <div
              onCopy={handleNativeCopy}
              className={cn(
                "text-pretty flex flex-col w-full min-w-0",
                isSelectedUser ? "items-end" : "items-start"
              )}
            >
              <div
                className={
                  isSelectedUser ? "w-[90%] min-w-0" : "w-full min-w-0"
                }
              >
                {message.user?.id ===
                  ReservedUserIds.ALERTS_CONVERSATIONAL_ID && (
                  <div className="p-2 pl-4">
                    <IconContainer
                      color="negative"
                      size="md"
                      className="drop-shadow-red-glow"
                    >
                      <Alert />
                    </IconContainer>
                  </div>
                )}

                {message.user?.id ===
                  ReservedUserIds.NOTIFICATIONS_CONVERSATIONAL_ID && (
                  <div className="p-2 pl-4">
                    <IconContainer
                      size="md"
                      color="natural"
                      className="drop-shadow-blue-glow"
                    >
                      <Notification />
                    </IconContainer>
                  </div>
                )}

                {/* Meeting prep leads with a plain-text greeting above the card; the brief
                    markdown in `message` is only the Slack/notification fallback, so skip it. */}
                {meetingPrepBrief ? (
                  <Text
                    as="p"
                    variant="lgBody"
                    className="px-5 whitespace-pre-line select-text"
                    data-test="meeting-prep-greeting"
                  >
                    {meetingPrepGreeting}
                  </Text>
                ) : (
                  messageBody
                )}
                {message.tabularMetadata?.display === "grid" ? (
                  <TableMessage
                    metadata={message.tabularMetadata}
                    messageId={message.id}
                    isSchedulable={!!message.isSchedulable}
                    isReplayable={!!message.isReplayable}
                    savedReportId={message.savedReportId ?? null}
                    scheduleId={message.scheduleId ?? null}
                    savedReportName={message.reportName ?? null}
                  />
                ) : isChartKind(message.tabularMetadata?.display) ? (
                  <ChartMessage
                    metadata={message.tabularMetadata}
                    messageId={message.id}
                    isSchedulable={!!message.isSchedulable}
                    isReplayable={!!message.isReplayable}
                    savedReportId={message.savedReportId ?? null}
                    scheduleId={message.scheduleId ?? null}
                    savedReportName={message.reportName ?? null}
                  />
                ) : null}

                {message.content?.meetingPrep ? (
                  <div className="px-5 pt-2">
                    <MeetingPrepCard brief={message.content.meetingPrep} />
                  </div>
                ) : null}

                {renderableDraft ? (
                  <div className="px-5 pt-2">
                    <DraftEmailCard
                      payload={renderableDraft}
                      isGenerating={isRegeneratingDraft}
                      onRegenerate={
                        onRegenerateDraft
                          ? (guidance) => onRegenerateDraft(message.id, guidance)
                          : undefined
                      }
                      onSelectVersion={
                        onSelectDraftVersion
                          ? (version, onFailure) =>
                              onSelectDraftVersion(message.id, version, onFailure)
                          : undefined
                      }
                    />
                  </div>
                ) : null}

                {isManageScheduledReports ? (
                  <div className="px-5 pt-2">
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() =>
                        opensReportsTable
                          ? setIsExpandedReportsOpen(true)
                          : setIsManageReportsModalOpen(true)
                      }
                      data-test="manage-scheduled-reports-button"
                    >
                      {t("chat.scheduleModal.manageSchedules")}
                    </Button>
                  </div>
                ) : null}

                {isShowScheduledReports ? (
                  <InlineScheduledReports
                    onExpand={() =>
                      opensReportsTable
                        ? setIsExpandedReportsOpen(true)
                        : setIsManageReportsModalOpen(true)
                    }
                  />
                ) : null}

                {opensReportsTable ? (
                  <ExpandedReportsModal
                    isOpen={isExpandedReportsOpen}
                    setIsOpen={setIsExpandedReportsOpen}
                    scheduledOnly
                  />
                ) : null}

                {/* Single modal mount shared by both the manage button and the
                    inline reports Expand — avoids double-mounting on one balloon. */}
                {(isManageScheduledReports || isShowScheduledReports) &&
                !opensReportsTable ? (
                  <RecurringReportsModal
                    isOpen={isManageReportsModalOpen}
                    setIsOpen={setIsManageReportsModalOpen}
                  />
                ) : null}

                {isDreamerUndoMessage && (
                  <div className="px-5 pt-2 flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="lg"
                      asChild
                      data-test="dreamer-view-task-button"
                    >
                      <Link to={`/tasks/${pushAction!.taskId}`}>
                        {t("chat.dreamerUndo.viewTask")}
                      </Link>
                    </Button>
                    {showMarkComplete && (
                      <Button
                        variant="secondary"
                        size="lg"
                        disabled={dreamerComplete.isPending}
                        onClick={() => {
                          dreamerComplete.mutate(
                            { pathParams: { taskId: pushAction!.taskId! } },
                            {
                              onSuccess: () => {
                                setIsHidden(true);
                                refreshTasks();
                              },
                              onError: () => {
                                toast.error(t("chat.dreamerUndo.markCompleteError"));
                              }
                            }
                          );
                        }}
                        data-test="dreamer-complete-button"
                      >
                        {t("chat.dreamerUndo.markComplete")}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="lg"
                      disabled={dreamerUndo.isPending}
                      onClick={() => {
                        dreamerUndo.mutate(
                          { pathParams: { taskId: pushAction!.taskId! } },
                          {
                            onSuccess: () => {
                              setIsHidden(true);
                              removeTask(pushAction!.taskId!);
                              removeTaskFromLists(pushAction!.taskId!);
                            },
                            onError: () => {
                              toast.error(t("chat.dreamerUndo.error"));
                            }
                          }
                        );
                      }}
                      data-test="dreamer-undo-button"
                    >
                      {t("chat.dreamerUndo.undo")}
                    </Button>
                  </div>
                )}

                {/* Data-fetching component mounted only when the action applies,
                    mirroring the modal mount pattern. */}
                {isDreamerLeadActionMessage && (
                  <DreamerLeadActions action={pushAction!} />
                )}

                {isDreamerLeadConvertMessage && (
                  <DreamerLeadConvertActions action={pushAction!} />
                )}

                {isDreamerReopenMessage && (
                  <div className="px-5 pt-2 flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="lg"
                      asChild
                      data-test="dreamer-reopen-view-task-button"
                    >
                      <Link to={`/tasks/${pushAction!.taskId}`}>
                        {t("chat.dreamerReopen.viewTask")}
                      </Link>
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      disabled={dreamerReopen.isPending}
                      onClick={() => {
                        dreamerReopen.mutate(
                          { pathParams: { taskId: pushAction!.taskId! } },
                          {
                            onSuccess: () => {
                              setIsHidden(true);
                              refreshTasks();
                            },
                            onError: () => {
                              toast.error(t("chat.dreamerReopen.error"));
                            }
                          }
                        );
                      }}
                      data-test="dreamer-reopen-button"
                    >
                      {t("chat.dreamerReopen.reopen")}
                    </Button>
                  </div>
                )}

                {message.isStoppedResponse && (
                  <div
                    className="flex items-center justify-between gap-2 mx-4 mt-2 px-3 py-2 rounded-lg border border-white-8 bg-white-4"
                    data-test="stopped-response-banner"
                  >
                    <div className="flex items-center gap-2">
                      <IconContainer size="sm" color="secondary">
                        <InfoCircle />
                      </IconContainer>
                      <Text variant="smBody" className="text-secondary">
                        {t("chat.responseStopped")}
                      </Text>
                    </div>
                    <IconButton
                      variant="iconNoBg"
                      size="sm"
                      iconColor="secondary"
                      data-test="retry-button"
                      onClick={handleRetry}
                      disabled={waitingForMessage}
                      tooltipText={t("chat.tooltips.retry")}
                    >
                      <Refresh />
                    </IconButton>
                  </div>
                )}

                <div className="flex flex-row gap-0 pt-1 px-4 self-stretch">
                  {isDreamerUser && !message.isStoppedResponse && (
                    <>
                      <IconButton
                        variant="iconNoBg"
                        size="sm"
                        iconColor="secondary"
                        data-test="star"
                        onClick={() => handleStar(message)}
                        tooltipText={t("chat.tooltips.star")}
                      >
                        {message.isStarred ? <StarFilled /> : <Star />}
                      </IconButton>
                      <IconButton
                        variant="iconNoBg"
                        size="sm"
                        iconColor="secondary"
                        data-test="copy"
                        onClick={() => handleCopy(message.message)}
                        tooltipText={
                          isCopied
                            ? t("chat.tooltips.copied")
                            : t("chat.tooltips.copy")
                        }
                        open={isOpen}
                        onMouseEnter={() => setIsOpen(true)}
                        onMouseLeave={() => setIsOpen(false)}
                      >
                        {isCopied ? <Check /> : <Copy />}
                      </IconButton>
                      <IconButton
                        variant="iconNoBg"
                        size="sm"
                        iconColor="secondary"
                        data-test="thumbs-up"
                        onClick={() => handleLike(message, true)}
                        tooltipText={t("chat.tooltips.thumbsUp")}
                      >
                        {message.isLiked === true ? (
                          <ThumbsUpFilled />
                        ) : (
                          <ThumbsUp />
                        )}
                      </IconButton>
                      <IconButton
                        variant="iconNoBg"
                        size="sm"
                        iconColor="secondary"
                        data-test="thumbs-down"
                        onClick={() => handleLike(message, false)}
                        tooltipText={t("chat.tooltips.thumbsDown")}
                      >
                        {message.isLiked === false ? (
                          <ThumbsDownFilled />
                        ) : (
                          <ThumbsDown />
                        )}
                      </IconButton>
                    </>
                  )}
                  <Text
                    variant="smBodySecondary"
                    className={cn(
                      "text-tertiary",
                      "self-center grow select-none",
                      isSelectedUser ? "text-start" : "text-end"
                    )}
                  >
                    {simpleTime(
                      new Date(parseInt(message.timestamp, 10) * 1000)
                    )}
                    {message.isStopped && (
                      <>
                        {" \u00b7 "}
                        <span className="text-on-surface-faint">
                          {t("chat.requestStopped")}
                        </span>
                      </>
                    )}
                  </Text>
                </div>
              </div>
            </div>
          </span>
        )}
      </div>
    </motion.div>
  );
}

// Custom comparison function to prevent re-renders when message content hasn't changed
function arePropsEqual(prevProps: Props, nextProps: Props): boolean {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.message === nextProps.message.message &&
    prevProps.message.isStarred === nextProps.message.isStarred &&
    prevProps.message.isLiked === nextProps.message.isLiked &&
    prevProps.message.isStopped === nextProps.message.isStopped &&
    prevProps.message.isStoppedResponse ===
      nextProps.message.isStoppedResponse &&
    prevProps.message.isError === nextProps.message.isError &&
    prevProps.message.thoughtMessage === nextProps.message.thoughtMessage &&
    prevProps.message.tabularMetadata === nextProps.message.tabularMetadata &&
    prevProps.message.inlineAction === nextProps.message.inlineAction &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.emailDraft === nextProps.message.emailDraft &&
    prevProps.message.isReplayable === nextProps.message.isReplayable &&
    prevProps.message.savedReportId === nextProps.message.savedReportId &&
    prevProps.message.scheduleId === nextProps.message.scheduleId &&
    prevProps.message.reportName === nextProps.message.reportName &&
    prevProps.onRegenerateDraft === nextProps.onRegenerateDraft &&
    prevProps.onRetryFailedAnswer === nextProps.onRetryFailedAnswer &&
    prevProps.onSelectDraftVersion === nextProps.onSelectDraftVersion &&
    prevProps.isRegeneratingDraft === nextProps.isRegeneratingDraft &&
    prevProps.selectedUser.id === nextProps.selectedUser.id &&
    prevProps.recipientFirstName === nextProps.recipientFirstName &&
    prevProps.onDownloadAttachment === nextProps.onDownloadAttachment &&
    prevProps.index === nextProps.index &&
    prevProps.skipAnimation === nextProps.skipAnimation &&
    prevProps.animateEntry === nextProps.animateEntry &&
    prevProps.enableLayoutExit === nextProps.enableLayoutExit
  );
}

export const ChatBalloon = memo(ChatBalloonComponent, arePropsEqual);
